import React from 'react';
import { Card, CardContent, Typography, Box, Link, Chip, alpha } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

interface Report {
  ticker: string;
  company_name: string;
  title: string;
  date_created: string;
  sentiment: string;
  summary: string;
  url?: string;
  report_id: string;
}

interface ReportCardProps {
  report: Report;
}

const generateLSEUrl = (reportId: string): string => {
  return `https://www.londonstockexchange.com/news-article/news-article/news-article/${reportId}`;
};

const getSentimentStyle = (sentiment: string): { color: string; bgColor: string; icon: React.ReactNode } => {
  switch (sentiment.toLowerCase()) {
    case 'positive':
      return {
        color: '#51CF66',
        bgColor: 'rgba(81, 207, 102, 0.15)',
        icon: <TrendingUpIcon sx={{ fontSize: '1rem', mr: 0.5 }} />,
      };
    case 'negative':
      return {
        color: '#FF6B6B',
        bgColor: 'rgba(255, 107, 107, 0.15)',
        icon: <TrendingDownIcon sx={{ fontSize: '1rem', mr: 0.5 }} />,
      };
    case 'neutral':
      return {
        color: '#FFB84D',
        bgColor: 'rgba(255, 184, 77, 0.15)',
        icon: <Typography sx={{ fontSize: '1rem', mr: 0.5 }}>—</Typography>,
      };
    default:
      return {
        color: '#B8C1D4',
        bgColor: 'rgba(184, 193, 212, 0.1)',
        icon: null,
      };
  }
};

const ReportCard: React.FC<ReportCardProps> = ({ report }) => {
  const sentimentStyle = getSentimentStyle(report.sentiment);

  const date = new Date(report.date_created).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return (
    <Card
      sx={{
        background: alpha('#1A1F2E', 0.8),
        border: '1px solid rgba(0, 217, 255, 0.1)',
        borderRadius: '12px',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(0, 217, 255, 0.3), transparent)',
        },
        '&:hover': {
          borderColor: 'rgba(0, 217, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 217, 255, 0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 4 }}>
        {/* Header Section - Ticker and Company */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <Box>
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
              <Typography
                sx={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #00D9FF 0%, #667EEA 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontFamily: "'Courier New', monospace",
                  letterSpacing: '0.05em',
                }}
              >
                {report.ticker}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                }}
              >
                {report.company_name}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={2} flexShrink={0}>
            <Chip
              label={sentimentStyle.icon ? (
                <Box display="flex" alignItems="center">
                  {sentimentStyle.icon}
                  {report.sentiment.charAt(0).toUpperCase() + report.sentiment.slice(1)}
                </Box>
              ) : (
                report.sentiment.charAt(0).toUpperCase() + report.sentiment.slice(1)
              )}
              sx={{
                backgroundColor: sentimentStyle.bgColor,
                color: sentimentStyle.color,
                fontWeight: 600,
                fontSize: '0.85rem',
                height: '28px',
                border: `1px solid ${sentimentStyle.color}33`,
              }}
            />
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: '0.8rem',
                fontWeight: 500,
                textAlign: 'right',
                minWidth: '120px',
              }}
            >
              {date}
            </Typography>
          </Box>
        </Box>

        {/* Title Section */}
        <Typography
          variant="h6"
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#FFFFFF',
            mb: 2,
            lineHeight: 1.4,
          }}
        >
          {report.title}
        </Typography>

        {/* Summary Section */}
        <Typography
          variant="body1"
          sx={{
            fontSize: '0.95rem',
            lineHeight: 1.7,
            color: 'rgba(255, 255, 255, 0.75)',
            mb: 3,
            maxWidth: '85ch',
          }}
        >
          {report.summary}
        </Typography>

        {/* Footer Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            pt: 3,
            borderTop: '1px solid rgba(0, 217, 255, 0.1)',
          }}
        >
          <Link
            href={generateLSEUrl(report.report_id)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              background: 'linear-gradient(135deg, #00D9FF 0%, #667EEA 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: 600,
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
                textDecoration: 'underline',
              },
            }}
          >
            View Full Report
            <OpenInNewIcon sx={{ ml: 0.75, fontSize: '0.9rem' }} />
          </Link>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReportCard;
